// groupLevels.ts · v12.10.15 (#1631 Call path never decides the Phone level word (levelNeutral)) · 2026-10-13 · VoiceSpectrum AI
// VoiceSpectrum.Web.T2 · v12.10.9 (Security Phase 1 – #1258/#1259/#1260/#1261) · 2026-10-06
// groupLevels.ts - the five access words (§32) and how a level is DERIVED from ticked actions (S17, §33).
// A group stores, per area, the action keys it allows. A level is a preset: every action whose tier is at or below it.
// When the ticks equal a preset the row shows that preset's name; anything else is Custom. Ported from the prototype
// (lvList / P / lvOf / customHint) with the area list coming from GET /api/t2/security/catalog.
import type { Area, Grants, Level, Tier } from '../../lib/securityApi';

export const LV: Record<Level, string> = { none: 'None', view: 'View', work: 'Work', full: 'Full', custom: 'Custom' };
export const LV_ICON: Record<Level, string> = { none: 'ban', view: 'eye', work: 'pen', full: 'badge', custom: 'puzzle' };
const RANK: Record<Exclude<Level, 'custom'>, number> = { none: 0, view: 1, work: 2, full: 3 };
const ORDER: Exclude<Level, 'custom'>[] = ['none', 'view', 'work', 'full'];

/** The levels a row offers, always None first and Custom last. */
export function lvList(a: Area): Level[] {
  const acts = a.actions ?? [];
  const derived: Level[] = ['none', 'view', ...(acts.some((x) => x.tier === 'work') ? ['work' as Level] : []), ...(acts.some((x) => x.tier === 'full') ? ['full' as Level] : []), 'custom'];
  const given = (a.levels ?? []).filter((l): l is Level => ['none', 'view', 'work', 'full', 'custom'].includes(l));
  if (!given.length) return derived;
  const set = new Set<Level>(['none', ...given, 'custom']);
  return (['none', 'view', 'work', 'full', 'custom'] as Level[]).filter((l) => set.has(l));
}

/** The action keys a preset level allows on this row (a level the row lacks falls back to the best one below it). */
export function preset(level: Level, a: Area): string[] {
  if (level === 'custom') return [];
  const L = lvList(a);
  let lv: Exclude<Level, 'custom'> = level;
  if (!L.includes(lv)) {
    let best: Exclude<Level, 'custom'> = 'none';
    ORDER.forEach((o) => { if (L.includes(o) && RANK[o] <= RANK[level as Exclude<Level, 'custom'>]) best = o; });
    lv = best;
  }
  const r = RANK[lv];
  return (a.actions ?? []).filter((x) => RANK[x.tier as Tier] <= r).map((x) => x.key);
}

function sameSet(a: string[], b: string[]): boolean { return a.length === b.length && a.every((x) => b.includes(x)); }

/** The level a set of ticks reads as. */
/** v12.10.15 (#1631): a tick that is granted like any other but never decides the level word (Phone calls › Call path). */
export function levelNeutral(areaKey: string, k: string): boolean { return areaKey === 'phone' && k === 'path'; }
const nz = (a: Area, xs: string[]) => xs.filter((x) => !levelNeutral(a.key, x));

export function levelOf(a: Area, v: string[] | undefined): Level {
  if (!v || !v.length) return 'none';
  const vv = nz(a, v);   // v12.10.15 (#1631): Office manager (every phone tick but Call path) reads Full
  if (!vv.length) return 'custom';
  for (const lv of lvList(a)) {
    if (lv === 'none' || lv === 'custom') continue;
    if (sameSet(vv, nz(a, preset(lv, a)))) return lv;
  }
  return 'custom';
}

export function actLabel(a: Area, k: string): string { return a.actions.find((x) => x.key === k)?.label ?? k; }

/** "Work, without Listen" / "View, plus Export" - the closest preset and the difference. */
export function customHint(a: Area, v: string[]): string {
  let best: { lv: Level; miss: string[]; extra: string[] } | null = null;
  let score = 99;
  for (const lv of lvList(a)) {
    if (lv === 'none' || lv === 'custom') continue;
    const p = nz(a, preset(lv, a));
    const miss = p.filter((x) => !v.includes(x));
    const extra = nz(a, v).filter((x) => !p.includes(x));
    const sc = miss.length + extra.length * 1.5;
    if (sc < score) { score = sc; best = { lv, miss, extra }; }
  }
  if (!best || (!best.miss.length && !best.extra.length)) return '';
  const f = (xs: string[]) => xs.map((x) => actLabel(a, x)).join(', ');
  return LV[best.lv] + (best.miss.length ? `, without ${f(best.miss)}` : '') + (best.extra.length ? `, plus ${f(best.extra)}` : '');
}

/** One-line meaning of a level on a row, for the pill tooltip. */
export function levelHelp(a: Area, lv: Level): string {
  if (lv === 'none') return 'Can’t open it - it isn’t in their menu';
  if (lv === 'custom') return 'Your own mix - tick exactly what’s allowed';
  const own = a.actions.filter((x) => x.tier === lv).map((x) => x.label).join(', ');
  if (lv === 'view') return `Read only: ${own || 'open and read'}`;
  return `${lv === 'work' ? 'View' : 'Work'} + ${own || (lv === 'full' ? 'delete' : 'everyday actions')}`;
}

/** Does `next` add anything to `cur` that `mine` (the viewer's own access) does not hold? (no escalation, §36) */
export function addsBeyond(cur: string[], next: string[], mine: string[] | undefined): boolean {
  if (!mine) return false;
  return next.some((k) => !cur.includes(k) && !mine.includes(k));
}

export function grantsEqual(a: Grants, b: Grants): boolean {
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
  for (const k of keys) {
    const x = (a[k] ?? []).slice().sort(), y = (b[k] ?? []).slice().sort();
    if (x.length !== y.length || x.some((v, i) => v !== y[i])) return false;
  }
  return true;
}
export function rowEqual(a: string[] | undefined, b: string[] | undefined): boolean {
  const x = (a ?? []).slice().sort(), y = (b ?? []).slice().sort();
  return x.length === y.length && x.every((v, i) => v === y[i]);
}
